//@ts-check
import { Countries, PhoneNumbersMap } from 'Constants/Common';
import { t } from 'i18next';
import { Panels } from './CosmeticRepairRequestTypes';
import { ValuesType } from 'types/generic';

export const ALLOWED_UK_PANEL_COMBINATIONS: Panels[][] = [
    [Panels.FrontBumper, Panels.Bonnet],
    [Panels.FrontBumper, Panels.FrontDriverWing],
    [Panels.FrontBumper, Panels.FrontPassengerWing],

    [Panels.Bonnet, Panels.FrontDriverWing],
    [Panels.Bonnet, Panels.FrontPassengerWing],

    [Panels.FrontDriverWing, Panels.FrontDriverDoor],
    [Panels.FrontDriverWing, Panels.DriverSill],

    [Panels.FrontPassengerWing, Panels.FrontPassengerDoor],
    [Panels.FrontPassengerWing, Panels.PassengerSill],
    [Panels.FrontPassengerWing, Panels.PassengerSill],

    [Panels.FrontDriverDoor, Panels.RearDriverDoor],
    [Panels.FrontDriverDoor, Panels.DriverSill],
    [Panels.FrontDriverDoor, Panels.Roof],

    [Panels.FrontPassengerDoor, Panels.RearPassengerDoor],
    [Panels.FrontPassengerDoor, Panels.PassengerSill],
    [Panels.FrontPassengerDoor, Panels.Roof],

    [Panels.RearDriverDoor, Panels.RearDriverWing],
    [Panels.RearDriverDoor, Panels.DriverSill],
    [Panels.RearDriverDoor, Panels.Roof],

    [Panels.RearPassengerDoor, Panels.RearPassengerWing],
    [Panels.RearPassengerDoor, Panels.PassengerSill],
    [Panels.RearPassengerDoor, Panels.Roof],

    [Panels.RearDriverWing, Panels.Boot],
    [Panels.RearDriverWing, Panels.DriverSill],
    [Panels.RearDriverWing, Panels.RearBumper],
    [Panels.RearDriverWing, Panels.Roof],

    [Panels.RearPassengerWing, Panels.Boot],
    [Panels.RearPassengerWing, Panels.PassengerSill],
    [Panels.RearPassengerWing, Panels.RearBumper],
    [Panels.RearPassengerWing, Panels.Roof],

    [Panels.Boot, Panels.RearBumper]
];

export const All_PANELS = Object.values(Panels);

const allowedPanelCombinationMap: {
    [key in ValuesType<typeof Countries>]?: Panels[][];
} = {
    [Countries.Uk]: ALLOWED_UK_PANEL_COMBINATIONS
};

const getAllowedPanelCombinations = (country: ValuesType<typeof Countries>) => {
    return allowedPanelCombinationMap[country] ?? [All_PANELS];
};

export const isPanelCombinationAllowed = (
    selectedPanels: Panels[],
    country: ValuesType<typeof Countries>
) => {
    if (selectedPanels.length <= 1) return true;

    return getAllowedPanelCombinations(country).some((combination) =>
        selectedPanels.every((panel) => combination.includes(panel))
    );
};

export const getNotAllowedSelectionMessage = (
    country: ValuesType<typeof Countries>
) => {
    if (country === Countries.Uk) {
        return t('selected_adjoining_panels', {
            phone: PhoneNumbersMap.cosmetics[country] || t('phone')
        });
    }
    return '';
};

const canAddPanel = (
    selectedPanels: Panels[],
    panel: Panels,
    country: ValuesType<typeof Countries>
): boolean => {
    return isPanelCombinationAllowed([...selectedPanels, panel], country);
};

/**
 * Returns the list of allowed panels to select from, based on the current selection.
 */
export const getAllowedPanels = (
    selectedPanels: Panels[],
    country: ValuesType<typeof Countries>
) => {
    return All_PANELS.filter((panel) =>
        canAddPanel(selectedPanels, panel, country)
    );
};
